/* LG Taste Quiz — framework-free. Same file runs in the standalone demo and the Shopify section.
   LGQuiz.mount(el, { pool, onAddToCart, onEmail, isKnown }) where pool = quiz-pool.json.
   onEmail(email, data) may return a promise (reject = show retry). isKnown() -> Promise<bool>
   defaults to window.klaviyo.isIdentified(); known visitors get a confirmation, not the ask. */
(function (global) {
  'use strict';

  const FLIGHT_DISCOUNT_PCT = 0.10; // "buy all 3 → 10% off". Keep in sync with the quiz-flight-discount Shopify Function.
  // Hidden line-item property that marks flight bottles; the quiz-flight-discount
  // Shopify Function applies 10% only to lines carrying it (and only while ≥3 are
  // in the cart). Underscore prefix hides it from the cart UI. No code to leak.
  const FLIGHT_PROP = { _lg_flight: '1' };
  // Marks every quiz-driven add-to-cart (single OR flight) so quiz orders are
  // attributable in Shopify by line-item property — no UTM or 44-second-timestamp
  // guessing. Search order line items for `_lg_quiz` = all quiz-sourced revenue.
  const QUIZ_PROP = { _lg_quiz: '1' };

  // ---- questions ----
  const FLAVOR_VIBES = {
    'Sweet & smooth':   ['sweet', 'caramel', 'vanilla', 'honey', 'smooth', 'creamy'],
    'Smoky & bold':     ['smoky', 'peat', 'oaky', 'toasted', 'briny'],
    'Bright & citrusy': ['citrus', 'floral', 'tropical'],
    'Spicy & warm':     ['spicy', 'peppery'],
    'Rich & oaky':      ['oaky', 'toasted', 'caramel', 'chocolate', 'dark-fruit', 'nutty', 'coffee'],
    'Fruity':           ['fruity', 'tropical', 'dark-fruit'],
    'Herbal & botanical': ['herbal', 'floral', 'juniper', 'agave', 'earthy'],
  };
  // quiz category -> set of pool categories. Tequila and Mezcal are separate picks
  // (different drinkers); Cognac & Brandy folds in Armagnac/Grappa (grape brandies).
  const CAT_MAP = {
    Whiskey: ['Whiskey'], Tequila: ['Tequila'], Mezcal: ['Mezcal'], Gin: ['Gin'],
    Rum: ['Rum'], Vodka: ['Vodka'], 'Cognac & Brandy': ['Cognac/Brandy', 'Armagnac', 'Grappa'],
    Liqueurs: ['Liqueur'],
  };

  // ---- adaptive flow ----
  // The follow-up questions REACT to the category pick instead of being one fixed list:
  //  - flavor vibes only show when the chosen shelves actually stock that style
  //    (a vodka drinker never sees "Smoky & bold" — the pool has zero smoky vodkas),
  //  - sweet/body questions are skipped entirely when they can't change the answer
  //    (every vodka is light; asking "Light or bold?" there is theater),
  //  - budget bands with almost nothing behind them are hidden (no "$250+" for liqueurs).
  // Skipped answers score neutrally — score() already ignores undefined sweet/body.
  //
  // CORE tags per vibe = the rare, discriminating markers used ONLY for deciding
  // whether to SHOW the option. Scoring still uses the full FLAVOR_VIBES lists
  // (oaky/toasted stay in "Smoky & bold" so Islay + big-oak whiskies rank right,
  // but 200 oaky bourbons shouldn't make the option appear for every shelf).
  const VIBE_CORE = {
    'Sweet & smooth':   ['honey', 'caramel', 'vanilla', 'creamy', 'smooth'],
    'Smoky & bold':     ['smoky', 'peat', 'briny'],
    'Bright & citrusy': ['citrus', 'tropical', 'floral'],
    'Spicy & warm':     ['spicy', 'peppery'],
    'Rich & oaky':      ['oaky', 'chocolate', 'dark-fruit', 'nutty', 'coffee'],
    'Fruity':           ['fruity', 'tropical', 'dark-fruit'],
    'Herbal & botanical': ['herbal', 'juniper', 'agave', 'earthy'],
  };
  const MIN_VIBE_BOTTLES = 6;   // vibe option needs this many bottles on the chosen shelves
  const MIN_BAND_BOTTLES = 6;   // budget band needs this many bottles to be offered

  function candidates(pool, ans) {
    const want = (ans.cats || []).flatMap(c => CAT_MAP[c] || []);
    return want.length ? pool.bottles.filter(b => want.includes(b.cat)) : pool.bottles;
  }
  function vibeVisible(pool, ans, label) {
    const core = VIBE_CORE[label] || [];
    let n = 0;
    for (const b of candidates(pool, ans)) {
      if (b.flavor.some(t => core.includes(t)) && ++n >= MIN_VIBE_BOTTLES) return true;
    }
    return false;
  }
  function sweetApplies(pool, ans) {
    // skip when ~everyone on these shelves sits in one 2-step sweetness window
    const cand = candidates(pool, ans);
    if (!cand.length) return true;
    const counts = {};
    for (const b of cand) counts[b.sweetness] = (counts[b.sweetness] || 0) + 1;
    let best = 0;
    for (let s = 1; s <= 4; s++) best = Math.max(best, (counts[s] || 0) + (counts[s + 1] || 0));
    return best / cand.length < 0.9;
  }
  function bodyApplies(pool, ans) {
    // skip when one body dominates the chosen shelves (all vodka is light, most gin too)
    const cand = candidates(pool, ans);
    if (!cand.length) return true;
    const counts = {};
    for (const b of cand) counts[b.body] = (counts[b.body] || 0) + 1;
    return Math.max(...Object.values(counts)) / cand.length < 0.85;
  }
  function bandVisible(pool, ans, band) {
    let n = 0;
    for (const b of candidates(pool, ans)) {
      if (b.price >= band[0] && b.price < band[1] && ++n >= MIN_BAND_BOTTLES) return true;
    }
    return false;
  }

  const QUESTIONS = [
    { key: 'cats', multi: true, q: 'What do you usually reach for?',
      sub: 'Pick any that sound like you — or just tell us you’re open.',
      options: [
        { label: 'Whiskey', emoji: '🥃' }, { label: 'Tequila', emoji: '🌵' },
        { label: 'Mezcal', emoji: '🌋' }, { label: 'Gin', emoji: '🌿' },
        { label: 'Rum', emoji: '🏝️' }, { label: 'Vodka', emoji: '❄️' },
        { label: 'Cognac & Brandy', emoji: '🍇' }, { label: 'Liqueurs', emoji: '🍫' },
        { label: 'I’m open to anything', emoji: '✨', clears: true },
      ] },
    { key: 'occasion', multi: false, q: 'How will you drink it?',
      options: [
        { label: 'Neat or on the rocks', emoji: '🧊', desc: 'Sipping the good stuff', val: 'sipping' },
        { label: 'In cocktails', emoji: '🍸', desc: 'Mixing it up', val: 'cocktails' },
        { label: 'As a gift', emoji: '🎁', desc: 'For someone special', val: 'gifting' },
        { label: 'After dinner', emoji: '🍮', desc: 'A sweet nightcap', val: 'dessert' },
      ] },
    { key: 'vibe', multi: true, q: 'Pick your flavor vibe',
      sub: 'Choose one or two that make your mouth water.',
      optionsFor: (pool, ans) => Object.keys(FLAVOR_VIBES)
        .filter(label => vibeVisible(pool, ans, label))
        .map(label => ({
          label, emoji: { 'Sweet & smooth': '🍯', 'Smoky & bold': '🔥', 'Bright & citrusy': '🍋',
            'Spicy & warm': '🌶️', 'Rich & oaky': '🪵', 'Fruity': '🍑', 'Herbal & botanical': '🌱' }[label] })),
      options: Object.keys(FLAVOR_VIBES).map(label => ({
        label, emoji: { 'Sweet & smooth': '🍯', 'Smoky & bold': '🔥', 'Bright & citrusy': '🍋',
          'Spicy & warm': '🌶️', 'Rich & oaky': '🪵', 'Fruity': '🍑', 'Herbal & botanical': '🌱' }[label] })) },
    { key: 'sweet', multi: false, q: 'Sweet or dry?', applies: sweetApplies,
      options: [
        { label: 'Bone dry', emoji: '🫙', val: 1 }, { label: 'Just a touch', emoji: '🍶', val: 2 },
        { label: 'Balanced', emoji: '⚖️', val: 3 }, { label: 'Sweet', emoji: '🍬', val: 4 },
        { label: 'Dessert-sweet', emoji: '🍰', val: 5 },
      ] },
    { key: 'body', multi: false, q: 'Light or bold?', applies: bodyApplies,
      options: [
        { label: 'Light & easy', emoji: '🌤️', val: 'light' },
        { label: 'Balanced', emoji: '🌗', val: 'medium' },
        { label: 'Big & bold', emoji: '🌑', val: 'bold' },
      ] },
    { key: 'price', multi: true, q: 'Budget per bottle?',
      sub: 'Pick any that work — choose as many as you like.',
      optionsFor: (pool, ans) => QUESTIONS[QUESTIONS.length - 1].options
        .filter(o => bandVisible(pool, ans, o.val)),
      options: [
        { label: 'Under $50', emoji: '💵', val: [0, 50] }, { label: '$50–100', emoji: '💸', val: [50, 100] },
        { label: '$100–250', emoji: '💳', val: [100, 250] }, { label: '$250+', emoji: '💎', val: [250, 1e9] },
      ] },
  ];

  // The questions that actually run for this taker: category-dependent skips applied.
  function activeQuestions(pool, ans) {
    return QUESTIONS.filter(Q => !Q.applies || Q.applies(pool, ans));
  }

  // ---- scoring ----
  function score(pool, ans) {
    const idf = pool.idf || {};
    const wantCats = (ans.cats || []).flatMap(c => CAT_MAP[c] || []);
    const wantFlavor = new Set((ans.vibe || []).flatMap(v => FLAVOR_VIBES[v] || []));
    const targetSweet = ans.sweet, targetBody = ans.body;
    // price is multi-select: ans.price is an array of band labels — map back to [min,max] ranges
    const PRICE_BANDS = Object.fromEntries(
      (QUESTIONS.find(q => q.key === 'price').options).map(o => [o.label, o.val]));
    const bands = (ans.price || []).map(l => PRICE_BANDS[l]).filter(Boolean);

    const scored = pool.bottles.map(b => {
      let s = 0;
      if (wantCats.length && !wantCats.includes(b.cat)) return { b, s: -Infinity, inb: 0 };
      // flavor match weighted by rarity (idf: rare tags like smoky/floral count far more than
      // "sweet"), but with DIMINISHING RETURNS on the summed match so a bottle that stacks many
      // tags can't dwarf the other four answers. A single rare-tag match still leads; sweet/body/
      // occasion stay strong enough to reorder bottles within a similar-flavor tier.
      let flavorRaw = 0;
      for (const t of b.flavor) if (wantFlavor.has(t)) flavorRaw += (idf[t] || 1);
      if (flavorRaw) s += Math.sqrt(flavorRaw) * 3.5;
      if (targetSweet != null) s -= Math.abs(b.sweetness - targetSweet) * 2;            // up to ~8
      if (targetBody) s += b.body === targetBody ? 4 : (adjacent(b.body, targetBody) ? 1.5 : 0);
      if (ans.occasion) s += (b.occasion || []).includes(ans.occasion) ? 3 : 0;
      // Budget is a CONSTRAINT, not a nudge: bottles inside a chosen band always rank
      // above out-of-band ones (see sort), so taste only decides order *within* budget.
      // inb=1 when no budget chosen (everything qualifies) or the price is in any band.
      let inb = 1;
      if (bands.length) {
        const inAny = bands.some(bd => b.price >= bd[0] && b.price < bd[1]);
        inb = inAny ? 1 : 0;
        if (!inAny) {
          // only matters for ordering the fallback when too few in-band bottles exist:
          // prefer the ones closest to the chosen band(s) (a $200 over a $30 for "$250+").
          const dist = Math.min(...bands.map(bd => b.price < bd[0] ? bd[0] - b.price : b.price - bd[1]));
          s -= dist / 50;
        }
      }
      s += Math.min(b.units || 0, 600) / 600 * 0.6; // gentle bestseller tiebreak
      return { b, s, inb };
    }).filter(x => x.s > -Infinity).sort((a, b) => b.inb - a.inb || b.s - a.s);

    // top 3 with brand diversity
    const out = [], brands = new Set();
    for (const x of scored) {
      const brand = x.b.title.toLowerCase().split(/\s+/).slice(0, 2).join(' ');
      if (brands.has(brand)) continue;
      brands.add(brand); out.push(x);
      if (out.length === 3) break;
    }
    while (out.length < 3 && scored[out.length]) out.push(scored[out.length]);
    return out;
  }
  function adjacent(a, b) { const o = ['light', 'medium', 'bold']; return Math.abs(o.indexOf(a) - o.indexOf(b)) === 1; }

  const UBIQUITOUS = new Set(['sweet', 'oaky', 'toasted', 'smooth', 'vanilla']);
  // Flavor chips for a pick: matched flavors first (emphasized), then other discriminating ones.
  function chipFlavors(b, wantFlavor) {
    const matched = b.flavor.filter(t => wantFlavor.has(t));
    const rest = b.flavor.filter(t => !wantFlavor.has(t) && !UBIQUITOUS.has(t));
    const restFallback = b.flavor.filter(t => !wantFlavor.has(t)); // if everything was ubiquitous
    const ordered = [...matched, ...(rest.length ? rest : restFallback)];
    return [...new Set(ordered)].slice(0, 4).map(t => ({ tag: t, on: wantFlavor.has(t) }));
  }

  // ---- taste profile: the named "flavor print" that heads the results ----
  // First vibe picked = the archetype; a second vibe lends its adjective ("The Honeyed Smoke
  // Chaser"); three or more = Flavor Explorer. On a gift the answers describe the giftee.
  const PROFILES = {
    'Smoky & bold':       { name: 'Smoke Chaser',   adj: 'Smoky',   line: 'campfire smoke and a little attitude' },
    'Sweet & smooth':     { name: 'Smooth Operator', adj: 'Honeyed', line: 'smooth, rounded sweetness: caramel, vanilla, honey' },
    'Bright & citrusy':   { name: 'Zest Seeker',    adj: 'Bright',  line: 'fresh citrus and florals that wake up the palate' },
    'Spicy & warm':       { name: 'Spice Hunter',   adj: 'Spiced',  line: 'pepper, baking spice and a long, warm finish' },
    'Rich & oaky':        { name: 'Barrel Devotee', adj: 'Oaky',    line: 'deep oak, dark fruit and a little chocolate' },
    'Fruity':             { name: 'Orchard Raider', adj: 'Juicy',   line: 'ripe fruit up front, from orchard to tropical' },
    'Herbal & botanical': { name: 'Botanist',       adj: 'Herbal',  line: 'herbs, botanicals and an earthy edge' },
  };
  const BODY_ORDER = ['light', 'medium', 'bold'];
  const SWEET_WORD = { 1: 'bone-dry', 2: 'off-dry', 3: 'right between dry and sweet', 4: 'on the sweeter side', 5: 'dessert-sweet' };
  const BODY_WORD = { light: 'light-bodied', medium: 'medium-bodied', bold: 'full-bodied' };
  const OCC_WORD = { sipping: 'best neat or on the rocks', cocktails: 'made for mixing', gifting: 'worth gifting', dessert: 'built for after dinner' };
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  function tasteProfile(pool, ans, picks) {
    const idf = pool.idf || {};
    const bs = picks.map(x => x.b);
    let vibes = (ans.vibe || []).filter(v => PROFILES[v]);
    if (!vibes.length) {
      // ?lgq= revisit carries no answers: read the profile off the three picks instead
      const weight = v => bs.reduce((s, b) => s + b.flavor.filter(t => VIBE_CORE[v].includes(t))
        .reduce((a, t) => a + (idf[t] || 1), 0), 0);
      vibes = Object.keys(PROFILES).map(v => [v, weight(v)]).filter(x => x[1] > 0)
        .sort((x, y) => y[1] - x[1]).slice(0, 2).map(x => x[0]);
    }
    let name, line;
    if (!vibes.length || vibes.length >= 3) {
      name = 'The Flavor Explorer'; line = 'a bit of everything, and a new favorite every time';
    } else {
      const p = PROFILES[vibes[0]], second = vibes[1] && PROFILES[vibes[1]];
      name = second ? `The ${second.adj} ${p.name}` : `The ${p.name}`;
      const adj = second && second.adj.toLowerCase();
      line = second ? `${p.line}, with ${/^([aeiou]|herb)/.test(adj) ? 'an' : 'a'} ${adj} streak` : p.line;
    }
    const gift = ans.occasion === 'gifting';
    // the sentence only states what they told us; the meters fall back to the picks' average
    const traits = [ans.body && BODY_WORD[ans.body], ans.sweet != null && SWEET_WORD[ans.sweet],
      ans.occasion && ans.occasion !== 'gifting' && OCC_WORD[ans.occasion]].filter(Boolean);
    const blurb = `${gift ? 'They’re' : 'You’re'} after ${line}.` + (traits.length ? ` ${cap(traits.join(', '))}.` : '');
    const avg = f => bs.reduce((s, b) => s + f(b), 0) / (bs.length || 1);
    const sweet = ans.sweet != null ? ans.sweet : Math.min(5, Math.max(1, Math.round(avg(b => b.sweetness || 3))));
    const body = ans.body || BODY_ORDER[Math.round(avg(b => Math.max(0, BODY_ORDER.indexOf(b.body))))] || 'medium';
    // signature notes: what the three picks share, the flavors they asked for first, rarest first
    const want = new Set(vibes.flatMap(v => FLAVOR_VIBES[v] || []));
    const count = {};
    for (const b of bs) for (const t of new Set(b.flavor)) count[t] = (count[t] || 0) + 1;
    const rank = t => (want.has(t) ? 100 : 0) + count[t] * (idf[t] || 1);
    let notes = Object.keys(count).filter(t => !UBIQUITOUS.has(t)).sort((x, y) => rank(y) - rank(x));
    if (notes.length < 3) notes = notes.concat(Object.keys(count).filter(t => UBIQUITOUS.has(t)).sort((x, y) => count[y] - count[x]));
    return { name, blurb, eyebrow: gift ? 'Their taste profile' : 'Your taste profile', sweet, body, notes: notes.slice(0, 4), vibes };
  }

  // ---- "why it's your match": each reason ties a pick back to a specific answer ----
  const NOTE_NOUN = { smoky: 'smoke', oaky: 'oak', toasted: 'toasted oak', sweet: 'sweetness', smooth: 'smoothness',
    fruity: 'fruit', 'dark-fruit': 'dark fruit', spicy: 'spice', tropical: 'tropical fruit', creamy: 'creaminess',
    peppery: 'pepper', herbal: 'herbs', earthy: 'earthy notes', nutty: 'nuttiness', floral: 'florals', briny: 'sea salt' };
  const SWEET_REASON = { 1: 'Bone-dry', 2: 'Just a touch sweet', 3: 'Balanced sweetness', 4: 'Sweet', 5: 'Dessert-sweet' };
  const BODY_REASON = { light: 'Light & easy', medium: 'Medium body', bold: 'Big & bold' };
  const OCC_REASON = { sipping: 'Made for sipping neat', cocktails: 'Mixes beautifully', gifting: 'A gift that impresses', dessert: 'A perfect nightcap' };
  function whyReasons(pool, b, ans) {
    const idf = pool.idf || {};
    const want = new Set((ans.vibe || []).flatMap(v => FLAVOR_VIBES[v] || []));
    const who = ans.occasion === 'gifting' ? 'they’re' : 'you’re';
    const out = [];
    // rarest matched notes first; skip one that just repeats the other ("dark fruit & fruit")
    const hits = [];
    for (const t of b.flavor.filter(t => want.has(t)).sort((x, y) => (idf[y] || 0) - (idf[x] || 0))) {
      const n = NOTE_NOUN[t] || t;
      if (hits.length < 2 && !hits.some(h => h.includes(n) || n.includes(h))) hits.push(n);
    }
    if (hits.length) out.push(`Brings the ${hits.join(' & ')} ${who} after`);
    if (ans.sweet != null) {
      const d = Math.abs(b.sweetness - ans.sweet);
      if (d === 0) out.push(`${SWEET_REASON[ans.sweet]}, right where you set it`);
      else if (d === 1) out.push(`Close to ${ans.occasion === 'gifting' ? 'their' : 'your'} sweet spot`);
    }
    if (ans.body && b.body === ans.body) out.push(`${BODY_REASON[ans.body]}, like you asked`);
    if (ans.occasion && (b.occasion || []).includes(ans.occasion)) out.push(OCC_REASON[ans.occasion]);
    const band = (QUESTIONS.find(q => q.key === 'price').options)
      .filter(o => (ans.price || []).includes(o.label))
      .find(o => b.price >= o.val[0] && b.price < o.val[1]);
    if (band) out.push(`Fits your ${band.label.replace(/^Under /, 'under-')} budget`);
    return out;
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }

  // ---- rendering ----
  function mount(el, opts) {
    const pool = opts.pool;
    const onAddToCart = opts.onAddToCart || defaultAdd;
    const onEmail = opts.onEmail || function () {};
    const _emit = opts.onEvent || function () {}; // funnel hook: (name, props) — never throws into the UI
    // Also send every funnel event straight to GA4 via gtag. The store's GTM container
    // does NOT forward the lgq_* dataLayer pushes to GA4, so without this the quiz funnel
    // (start→step→complete→add) never reaches GA4 and is unmeasurable there. gtag is global
    // (GA4 is loaded via GTM). Flat scalar params only — GA4 drops nested objects.
    const track = function (name, props) {
      try {
        if (typeof window.gtag === 'function') {
          const gp = { quiz: 'taste-quiz' };
          if (props) {
            if (props.value != null) gp.value = props.value;
            if (props.price != null) gp.price = props.price;
            if (props.step != null) gp.step = props.step;
            if (props.question != null) gp.question = props.question;
            if (props.rank != null) gp.rank = props.rank;                 // 1 = the hero pick
            if (props.taste_profile) gp.taste_profile = props.taste_profile;
          }
          window.gtag('event', 'lgq_' + name, gp);
        }
      } catch (e) {}
      return _emit(name, props);
    };
    const RESULTS = 999; // step sentinel: past the last active question
    const ans = {}; let step = -1; // -1 = intro
    // ?lgq=<id,id,id> deep-link (from the picks email): jump straight to those
    // results so "Add the flight" works — with the 10% — without retaking the quiz.
    let presetPicks = null;
    try {
      const ids = (new URLSearchParams(location.search).get('lgq') || '').split(',').filter(Boolean);
      if (ids.length === 3) {
        const found = ids.map(id => pool.bottles.find(b => String(b.id) === id)).filter(Boolean);
        if (found.length === 3) { presetPicks = found.map(b => ({ b, s: 0 })); step = RESULTS; }
      }
    } catch (e) {}
    const root = document.createElement('div'); root.className = 'lgq'; el.appendChild(root);
    render();
    if (presetPicks) { track('quiz_revisit', { picks: presetPicks.map(x => x.b.title) }); scrollTop(); }

    function render() {
      if (step === -1) return renderIntro();
      const act = activeQuestions(pool, ans);
      if (step >= act.length) return renderResults();
      const Q = act[step];
      // options can depend on earlier answers (vibes for the chosen shelves, live budget bands)
      const opts = Q.optionsFor ? Q.optionsFor(pool, ans) : Q.options;
      // prune selections whose option is no longer offered (user went Back and changed category)
      if (Q.multi && ans[Q.key]) {
        const visible = new Set(opts.map(o => o.label));
        const pruned = ans[Q.key].filter(l => visible.has(l));
        if (pruned.length !== ans[Q.key].length) { ans[Q.key] = pruned; ans['_' + Q.key] = pruned; }
      }
      const sel = ans[Q.key];
      const oneCol = opts.length <= 5 && opts.some(o => o.desc);
      root.innerHTML = `
        <div class="lgq-step">
          <div class="lgq-top">
            <div class="lgq-prog"><i style="width:${((step + 1) / act.length) * 100}%"></i></div>
            <span class="lgq-count">Question ${step + 1} of ${act.length}</span>
          </div>
          <h2>${esc(Q.q)}</h2>${Q.sub ? `<p class="lgq-sub">${esc(Q.sub)}</p>` : '<div style="height:6px"></div>'}
          <div class="lgq-opts ${oneCol ? 'cols-1' : ''}" role="group" aria-label="${esc(Q.q)}">
            ${opts.map((o, i) => optHtml(Q, o, i, sel)).join('')}
          </div>
          <div class="lgq-nav">
            <button class="lgq-btn ghost" data-back>← Back</button>
            <button class="lgq-btn primary" data-next ${ready(Q) ? '' : 'disabled'}>${step === act.length - 1 ? 'See my bottles →' : 'Next'}</button>
          </div>
        </div>`;
      root.querySelectorAll('[data-opt]').forEach(b => b.onclick = () => pick(Q, opts, +b.dataset.opt));
      root.querySelector('[data-next]').onclick = () => { if (ready(Q)) { track('quiz_step', { step: step + 1, of: act.length, question: Q.key }); step++; render(); scrollTop(); } };
      root.querySelector('[data-back]').onclick = () => { step--; render(); scrollTop(); };
    }

    function renderIntro() {
      root.innerHTML = `
        <div class="lgq-step lgq-intro">
          <div class="lgq-stripe" aria-hidden="true"><span>🥃</span><span>🍸</span><span>🌵</span><span>🏝️</span><span>🍫</span></div>
          <span class="lgq-badge">Bottle Match · by the Geeks</span>
          <h2>Find your next favorite bottle</h2>
          <p class="lgq-sub">A few quick questions. 3 bottles matched to your taste by our experts — from ${pool.bottles.length}+ hand-picked spirits. Adventure in every bottle.</p>
          <button class="lgq-btn primary" data-start>Start the quiz →</button>
          <p class="lgq-meta">Takes about 60 seconds · No account needed</p>
        </div>`;
      root.querySelector('[data-start]').onclick = () => { track('quiz_start', {}); step = 0; render(); scrollTop(); };
    }

    function optHtml(Q, o, i, sel) {
      // compare by JSON so single-select highlights work for string/number/array values alike
      const on = Q.multi ? (sel || []).includes(o.label) : JSON.stringify(sel) === JSON.stringify(valOf(o));
      return `<button class="lgq-opt ${on ? 'sel' : ''}" data-opt="${i}" role="${Q.multi ? 'checkbox' : 'radio'}" aria-checked="${on}">
        ${o.emoji ? `<span class="emoji" aria-hidden="true">${o.emoji}</span>` : ''}
        <span class="lgq-ol"><span>${esc(o.label)}</span>${o.desc ? `<span class="lgq-od">${esc(o.desc)}</span>` : ''}</span>
        <span class="check" aria-hidden="true"></span></button>`;
    }
    function valOf(o) { return o.val !== undefined ? o.val : o.label; }
    function pick(Q, opts, i) {
      const o = opts[i];
      if (Q.multi) {
        let arr = ans[Q.key] ? ans[Q.key].slice() : [];
        if (o.clears) { arr = arr.includes(o.label) ? [] : [o.label]; }
        else { const ci = arr.indexOf(o.label);
          ci >= 0 ? arr.splice(ci, 1) : arr.push(o.label);
          arr = arr.filter(x => !(opts.find(z => z.label === x) || {}).clears); }
        ans[Q.key] = arr;
        ans['_' + Q.key] = arr; // keep labels for Klaviyo
      } else { ans[Q.key] = valOf(o); }
      render();
    }
    function ready(Q) { const v = ans[Q.key]; return Q.multi ? (v && v.length) : (v !== undefined); }
    function scrollTop() { try { root.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {} }

    function renderResults() {
      // normalize cats: "open to anything" -> no filter
      const a = Object.assign({}, ans);
      if ((a.cats || []).some(c => (QUESTIONS[0].options.find(o => o.label === c) || {}).clears)) a.cats = [];
      // a skipped question keeps whatever was answered before Back + a category change
      // (whiskey "dessert-sweet" → vodka): drop it so neither the scoring nor the profile uses it
      const act = activeQuestions(pool, a);
      for (const Q of QUESTIONS) if (Q.applies && !act.includes(Q)) delete a[Q.key];
      const wantFlavor = new Set((a.vibe || []).flatMap(v => FLAVOR_VIBES[v] || []));
      const picks = presetPicks || score(pool, a);
      const profile = tasteProfile(pool, a, picks);
      // quiz_complete carries the FULL pick objects (not just titles) + flavor_vibe so the
      // Klaviyo "Your Picks" flow (WqZ53z) renders product cards + the ?lgq= deep-link even
      // when it triggers on this event (cookied browsers, no email-capture needed). Mirrors the
      // server-side "Completed Taste Quiz" payload so the template renders identically either way.
      const richPicks = picks.map(x => ({
        id: x.b.id, title: x.b.title, url: x.b.url || '', image: x.b.image || '',
        price: x.b.price, notes: x.b.notes || '',
      }));
      if (!presetPicks) track('quiz_complete', {
        spirits: a._cats || a.cats, flavor_vibe: a._vibe || a.vibe,
        occasion: a.occasion, sweetness: a.sweet, body: a.body, taste_profile: profile.name,
        recommended: picks.map(x => x.b.title), picks: richPicks,
      });
      const total = picks.reduce((s, x) => s + x.b.price, 0);
      const pctLabel = Math.round(FLIGHT_DISCOUNT_PCT * 100);
      // What checkout actually charges: the flight Function's 10% is applied per line and the
      // cents are floored (224.99 → 22.49 off), so total × 0.9 overstated the deal by a few cents.
      const discounted = picks.reduce((s, x) => {
        const c = Math.round(x.b.price * 100);
        return s + c - Math.floor(c * Math.round(FLIGHT_DISCOUNT_PCT * 100) / 100);
      }, 0) / 100;
      // Budget honesty: when a budget was set but the chosen style is thin at that price,
      // some picks stretch outside it (closest-priced fallback). Say so instead of pretending.
      const outOfBudget = (!presetPicks && (a.price || []).length) ? picks.filter(x => x.inb === 0).length : 0;
      const inBudget = picks.length - outOfBudget;
      const budgetNote = outOfBudget
        ? `<p class="lgq-note">${inBudget
            ? `${inBudget} of these land in your budget — the other ${outOfBudget} ${outOfBudget === 1 ? 'is the closest match' : 'are the closest matches'} we'd still stand behind.`
            : `We're light on this style at that budget — here are the closest bottles we'd still recommend.`}</p>`
        : '';
      root.innerHTML = `
        <div class="lgq-step">
          <p class="lgq-eyebrow">${esc(profile.eyebrow)}</p>
          <h2>${esc(profile.name)}</h2>
          <p class="lgq-sub">${esc(profile.blurb)}</p>
          ${printHtml(profile)}
          ${budgetNote}
          ${presetPicks ? '' : `<button class="lgq-savepill" data-jumpemail hidden>📩 Email me these picks</button>`}
          <div class="lgq-results">${picks.map((x, i) => (i === 1 ? '<p class="lgq-more">Round out the flight</p>' : '') + cardHtml(x.b, a, wantFlavor, i)).join('')}</div>
          <div class="lgq-bundle">
            <div><div class="bt">Make it a flight of ${picks.length} 🥃</div><div class="bs">Save ${pctLabel}% when you grab all three together</div></div>
            <div class="bprice"><span class="was">$${total.toFixed(2)}</span><span class="bp">$${discounted.toFixed(2)}</span></div>
            <button class="lgq-btn gold" data-bundle>Add the flight · ${pctLabel}% off</button>
          </div>
          ${presetPicks ? '' : `<div class="lgq-email" data-emailbox hidden>
            <div data-emailknown hidden>
              <div class="el">📩 Sending these to your inbox</div>
              <div class="es">We'll email these ${picks.length} to the address we have for you, with a one-tap link that rebuilds this flight at ${pctLabel}% off. <button class="lgq-linkbtn" data-otheremail>Not you? Use your email</button></div>
            </div>
            <div data-emailask hidden>
              <div class="el">Save your matches 📩</div>
              <div class="es">We'll email your ${picks.length} picks with a one-tap link that rebuilds this flight at ${pctLabel}% off, so you can grab it whenever you're ready.</div>
              <form class="erow" data-emailform novalidate>
                <input type="email" name="email" autocomplete="email" inputmode="email" placeholder="you@email.com" data-email aria-label="Email address" aria-describedby="lgq-eerr" />
                <button type="submit" class="lgq-btn primary" data-sendemail>Email my picks</button>
              </form>
              <p class="eerr" id="lgq-eerr" data-eerr role="alert"></p>
              <p class="efine">You'll also get Liquor Geeks emails, like first dibs on new bottles for your palate. Unsubscribe anytime.</p>
            </div>
            <div data-emailsent hidden>
              <div class="el" tabindex="-1" data-sentfocus>Sent! Check your inbox 📩</div>
              <div class="es" data-sentto></div>
            </div>
          </div>`}
          <p class="lgq-foot"><button class="lgq-restart" data-restart>↺ Retake the quiz</button></p>
        </div>`;
      root.querySelectorAll('[data-add]').forEach(btn => btn.onclick = () => {
        const at = picks.findIndex(x => String(x.b.variantId) === btn.dataset.add);
        const picked = picks[at];
        // rank 1 = the hero pick: tells us whether leading with one bottle actually moves adds
        track('quiz_add_single', { title: picked ? picked.b.title : '', price: picked ? picked.b.price : null,
          rank: at + 1, taste_profile: profile.name });
        onAddToCart([{ id: btn.dataset.add, quantity: 1, properties: Object.assign({}, QUIZ_PROP) }], btn);
      });
      root.querySelector('[data-bundle]').onclick = (e) => {
        track('quiz_add_flight', { titles: picks.map(x => x.b.title), value: +discounted.toFixed(2), taste_profile: profile.name });
        // FLIGHT_PROP is what arms the 10% Shopify Function discount on these lines
        onAddToCart(picks.map(x => ({ id: x.b.variantId, quantity: 1, properties: Object.assign({}, FLIGHT_PROP, QUIZ_PROP) })), e.currentTarget);
      };
      if (!presetPicks) wireEmail(a, richPicks, profile.name); // ?lgq= revisits came from the picks email: no ask
      root.querySelector('[data-restart]').onclick = () => { presetPicks = null; for (const k in ans) delete ans[k]; step = -1; render(); scrollTop(); };
    }
    // ---- email capture (2026-10-12 redesign) ----
    // Two states, decided on each results render:
    //  - KNOWN: Klaviyo already identifies this browser (Alia signup, email click). The
    //    quiz_complete track above landed on their profile and flow WqZ53z is already
    //    emailing these picks, so confirm that instead of asking again. 9/12-10/11: 21 of
    //    ~38 completers were known to Klaviyo, and 4 of the 6 emails typed since July
    //    came from people who were already getting the email.
    //  - ASK: everyone else. Their quiz_complete track was dropped while anonymous, so
    //    onEmail has to fire the flow trigger itself (before 2026-10-12 it didn't, and
    //    typed emails never got their picks).
    // onEmail(email, { answers, picks, eventId }) may return a promise. A rejection shows a
    // retry message instead of "Sent"; err.status 4xx = Klaviyo refused the address.
    // eventId is fixed per results render: the section sends it as Klaviyo's unique_id, so a
    // retry after a dropped connection can't trigger a second picks email.
    let emailIO = null;
    function wireEmail(a, richPicks, profileName) {
      const box = root.querySelector('[data-emailbox]');
      const known = box.querySelector('[data-emailknown]');
      const ask = box.querySelector('[data-emailask]');
      const sent = box.querySelector('[data-emailsent]');
      const pill = root.querySelector('[data-jumpemail]');
      const form = box.querySelector('[data-emailform]');
      const input = box.querySelector('[data-email]');
      const btn = box.querySelector('[data-sendemail]');
      const err = box.querySelector('[data-eerr]');
      const eventId = 'lgq-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      if (emailIO) { emailIO.disconnect(); emailIO = null; }
      function showAsk(viaOther) {
        known.hidden = true; ask.hidden = false;
        if (pill && !viaOther) pill.hidden = false;
        // seen-vs-submitted: tells "never scrolled to it" apart from "saw it, said no"
        if ('IntersectionObserver' in window) {
          emailIO = new IntersectionObserver(es => {
            if (!es.some(x => x.isIntersecting)) return;
            track('quiz_email_view', {}); emailIO.disconnect(); emailIO = null;
          }, { threshold: 0.6 });
          emailIO.observe(ask);
        }
      }
      function fail(text) { input.setAttribute('aria-invalid', 'true'); err.textContent = text; }
      // the box stays hidden until Klaviyo answers, so no empty card flashes in
      (opts.isKnown || klaviyoKnows)().then(isKnown => {
        if (!box.isConnected) return; // retook the quiz before Klaviyo answered
        box.hidden = false;
        if (isKnown) { known.hidden = false; track('quiz_email_known', {}); }
        else showAsk(false);
      });
      // "Not you?" (not "send to someone else"): the form subscribes whoever is typed in
      box.querySelector('[data-otheremail]').onclick = () => { showAsk(true); input.focus(); };
      if (pill) pill.onclick = () => {
        track('quiz_email_jump', {});
        try { ask.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
        setTimeout(() => { try { input.focus({ preventScroll: true }); } catch (e) {} }, 400);
      };
      form.onsubmit = (e) => {
        e.preventDefault();
        const email = input.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { fail("That email doesn't look right. Mind checking it?"); input.focus(); return; }
        input.removeAttribute('aria-invalid'); err.textContent = '';
        btn.disabled = true; btn.textContent = 'Sending…';
        track('quiz_email', {});
        // full pick detail so the Klaviyo "your picks" flow email can render
        // image / price / link without a product-feed lookup
        new Promise(res => res(onEmail(email, { answers: a, picks: richPicks, eventId, profile: profileName }))).then(() => {
          if (!box.isConnected) return;
          box.querySelector('[data-sentto]').textContent = `Your picks are on their way to ${email}, flight link included.`;
          ask.hidden = true; sent.hidden = false;
          if (pill) pill.hidden = true;
          try { box.querySelector('[data-sentfocus]').focus({ preventScroll: true }); } catch (e) {}
        }, (x) => {
          track('quiz_email_error', {});
          if (!box.isConnected) return;
          btn.disabled = false; btn.textContent = 'Email my picks';
          const refused = x && x.status >= 400 && x.status < 500;
          fail(refused ? "That address didn't go through. Mind checking it?"
            : "Couldn't reach our mail service. Please try again in a moment.");
        });
      };
    }
    function klaviyoKnows() {
      return new Promise(res => {
        setTimeout(() => res(false), 1500); // klaviyo.js slow or blocked: ask
        try {
          const k = window.klaviyo;
          if (k && typeof k.isIdentified === 'function') Promise.resolve(k.isIdentified()).then(v => res(v === true), () => res(false));
          else res(false);
        } catch (e) { res(false); }
      });
    }
    // Pick 1 is the HERO: bigger card, full "why it's your match" list. Picks 2-3 keep the
    // compact card with their two strongest reasons inline. Deep-link revisits have no
    // answers, so no reasons; the tasting note carries those cards.
    function cardHtml(b, a, wantFlavor, i) {
      const hero = i === 0;
      const rank = ['Your top match', '2nd pick', '3rd pick'][i] || 'Pick';
      const chips = chipFlavors(b, wantFlavor)
        .map(c => `<span class="lgq-chip ${c.on ? 'on' : ''}">${esc(c.tag.replace('-', ' '))}</span>`).join('');
      const img = b.image
        ? `<img src="${esc(b.image)}" alt="${esc(b.title)}" loading="${hero ? 'eager' : 'lazy'}"/>`
        : `<span style="font-size:26px" aria-hidden="true">🥃</span>`;
      const why = presetPicks ? [] : whyReasons(pool, b, a).slice(0, hero ? 4 : 2);
      const whyHtml = !why.length ? ''
        : hero ? `<div class="lgq-why"><span class="wl">Why it’s your match</span><ul>${why.map(r => `<li>${esc(r)}</li>`).join('')}</ul></div>`
        : `<ul class="lgq-why lgq-why--inline">${why.map(r => `<li>${esc(r)}</li>`).join('')}</ul>`;
      return `<div class="lgq-card${hero ? ' lgq-card--hero' : ''}">
        <span class="ph">${img}</span>
        <div>
          <span class="rank">${rank}</span>
          <div class="t">${esc(b.title)}</div>
          <div class="lgq-chips">${chips}</div>
          ${b.notes ? `<div class="note">“${esc(b.notes)}”</div>` : ''}
          ${whyHtml}
        </div>
        <div class="col-r">
          <div class="pr">$${b.price.toFixed(2)}</div>
          <button class="add" data-add="${esc(b.variantId)}">Add to cart</button>
          ${b.url ? `<a class="view" href="${esc(b.url)}" target="_blank" rel="noopener">View details →</a>` : ''}
        </div>
      </div>`;
    }
    // The flavor print: where they sit on dry↔sweet and light↔bold, plus the notes
    // their three bottles share.
    function printHtml(p) {
      const sweetPct = ((p.sweet - 1) / 4) * 100;
      const bodyPct = (BODY_ORDER.indexOf(p.body) / 2) * 100;
      const meter = (lo, hi, pct, label) => `
        <div class="lgq-meter" role="img" aria-label="${esc(label)}">
          <div class="ml"><span>${lo}</span><span>${hi}</span></div>
          <div class="mt"><i style="left:${pct}%"></i></div>
        </div>`;
      const notes = p.notes.map(t => `<span class="lgq-chip on">${esc(t.replace('-', ' '))}</span>`).join('');
      return `<div class="lgq-print">
        <div class="lgq-meters">
          ${meter('Dry', 'Sweet', sweetPct, 'Sweetness: ' + SWEET_WORD[p.sweet])}
          ${meter('Light', 'Bold', bodyPct, 'Body: ' + BODY_WORD[p.body])}
        </div>
        ${notes ? `<div class="lgq-sig"><span class="sl">Signature notes</span><div class="lgq-chips">${notes}</div></div>` : ''}
      </div>`;
    }
  }

  // default add-to-cart: Shopify AJAX API (works inside the theme). The flight's
  // 10% comes from the quiz-flight-discount Shopify Function (automatic, reads the
  // _lg_flight line property) — no discount-code URL involved.
  // The store runs Rebuy Smart Cart: open the side cart in place instead of
  // navigating to /cart (fall back to the redirect when Rebuy isn't on the page).
  function defaultAdd(items, btn) {
    if (btn) btn.textContent = 'Adding…';
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }) })
      .then(r => { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(() => {
        const sc = window.Rebuy && window.Rebuy.SmartCart;
        if (sc && typeof sc.show === 'function') {
          if (btn) btn.textContent = 'Added ✓';
          // sync the Smart Cart with the AJAX-added lines, then open it
          fetch('/cart.js').then(r => r.json())
            .then(cart => { if (typeof sc.setCart === 'function') sc.setCart(cart); sc.show(); })
            .catch(() => sc.show());
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(() => { if (btn) btn.textContent = 'Try again'; });
  }

  global.LGQuiz = { mount, score, QUESTIONS, FLAVOR_VIBES, CAT_MAP, VIBE_CORE, activeQuestions, candidates, tasteProfile, whyReasons };
})(window);
